import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import jwtDecode from "jwt-decode";
import AttachFileIcon from '@material-ui/icons/AttachFile';
import moment from "moment";
import { useStyles } from './ChatBoxStyles';

const ChatBox = (props) => {
  const classes = useStyles();
  const { batchNumber } = props;
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const tokenData = jwtDecode(localStorage.getItem("authToken"));
  const [file, setFile] = useState(null);
  const chatContainerRef = useRef(null);
  const [sendMessageLoading, setMessageLoading] = useState(false);

  const getFirstBatchNumber = () => {
    if (!batchNumber) return "400682";
    const parts = batchNumber.split(',');
    return parts[0].trim();
  };

  const scrollChatToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() !== "" || file) {
      setMessageLoading(true);
      
      const newMsg = {
        sender: tokenData.full_name || "You",
        time: moment().format("MMM DD, YYYY, HH:mm"),
        text: newMessage,
        type: "sent",
        file: file ? file.name : null,
      };

      setMessages([...messages, newMsg]);
      setNewMessage("");
      setFile(null);
      setMessageLoading(false);
      
      setTimeout(() => {
        scrollChatToBottom();
      }, 100);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setTimeout(() => {
        const currentUserEmail = tokenData.email_address || "";
        const sampleMessages = [
          {
            sender: "Terri Cooper",
            time: "Jun 21, 2024, 09:02",
            text: "We expect to deliver Primaxin on 4/25 instead of 4/15.",
            type: "sent",
            file: null,
          },
          {
            sender: "Mark Davenport",
            time: "Jun 24, 2024, 14:25",
            text: "Okay",
            type: "received",
            file: null,
          },
        ];
        setMessages(sampleMessages);
        setLoading(false);
        setTimeout(() => {
          scrollChatToBottom();
        }, 100);
      }, 500);
    }
  }, [isOpen]);

  return (
    <>
      <Box
        position="fixed"
        bottom="0"
        right="20px"
        width="350px"
        boxShadow={3}
        borderRadius={isOpen ? "10px 10px 0 0" : "20px 20px 0 0"}
        overflow="hidden"
        bgcolor="white"
        zIndex={1000}
      >
        {!isOpen && (
          <Box
            bgcolor="#2196f3"
            color="white"
            padding="10px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setIsOpen(true)}
            style={{ cursor: "pointer" }}
          >
            <Typography variant="subtitle1" style={{ fontSize: "1rem" }}>
              Chat for Batch Number {getFirstBatchNumber()}
            </Typography>
            <IconButton
              size="small"
              style={{ color: "white" }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </Box>
        )}

        {isOpen && (
          <Box display="flex" flexDirection="column" height="400px">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              bgcolor="#2196f3"
              color="white"
              padding="10px"
              borderRadius="10px 10px 0 0"
              style={{ cursor: "pointer" }}
              onClick={() => setIsOpen(false)}
            >
              <Typography variant="h6" style={{ fontSize: "1rem" }}>
                Chat for Batch Number {getFirstBatchNumber()}
              </Typography>
              <IconButton
                size="small"
                style={{ color: "white" }}
                onClick={() => setIsOpen(false)}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Box>

            <Box
              flexGrow="1"
              padding="10px"
              overflow="auto"
              bgcolor="#e7ecee"
              ref={chatContainerRef}
            >
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "5rem",
                  }}
                >
                  <CircularProgress color="primary" thickness={7} />
                </div>
              ) : error ? (
                <div
                  style={{
                    border: "2px solid rgb(255,0,0)",
                    backgroundColor: "rgb(255,240,240)",
                    Opacity: 1,
                    textAlign: "center",
                    padding: "0.8rem",
                    fontSize: "0.875rem",
                    marginTop: "4rem",
                  }}
                >
                  <Typography color="error" style={{ textAlign: "center", margin: "10px 0" }}>
                    {error}
                  </Typography>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <Box
                    key={index}
                    display="flex"
                    flexDirection="column"
                    alignItems={msg.type === "sent" ? "flex-end" : "flex-start"}
                    marginBottom="15px"
                  >
                    <Box
                      bgcolor={msg.type === "sent" ? "#dcf8c6" : "#ffffff"}
                      color="black"
                      padding="10px"
                      borderRadius="15px"
                      maxWidth="80%"
                      boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
                      style={{
                        alignSelf: msg.type === "sent" ? "flex-end" : "flex-start",
                        marginLeft: msg.type === "received" ? "10px" : "auto",
                        marginRight: msg.type === "sent" ? "10px" : "auto",
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: "bold",
                          color: msg.type === "sent" ? "#ef7b52" : "green",
                        }}
                      >
                        {msg.sender}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: "#929292",
                          textAlign: "left",
                          fontSize: "0.75rem",
                        }}
                      >
                        {msg.time}
                      </Typography>
                      <Typography variant="body2" style={{ marginBottom: "5px" }}>
                        {msg.text}
                      </Typography>
                      {msg.file && (
                        <Typography
                          variant="body2"
                          className={classes.link}
                          style={{
                            textDecoration: "underline",
                            display: "flex",
                          }}
                        >
                          {msg.file}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              padding="0px"
              borderTop="1px solid #ddd"
              borderLeft="1px solid #ddd"
              borderRight="1px solid #ddd"
              borderBottom="1px solid #ddd"
              marginLeft="-1px"
              marginRight="-1px"
              marginBottom="-1px"
              width="calc(100% + 2px)"
            >
              {file && (
                <Box
                  display="flex"
                  alignItems="center"
                  bgcolor="#f1f1f1"
                  padding="5px 10px"
                  borderRadius="5px"
                  marginBottom="5px"
                  marginLeft="8px"
                  marginRight="8px"
                  marginTop="8px"
                  width="calc(100% - 16px)"
                >
                  <Typography variant="body2" style={{ marginRight: "10px" }}>
                    {file.name}
                  </Typography>
                  <IconButton size="small" onClick={() => setFile(null)}>
                    <Typography style={{ fontSize: "0.75rem" }}>×</Typography>
                  </IconButton>
                </Box>
              )}

              <Box
                display="flex"
                alignItems="center"
                padding="12px"
                border="1px solid black"
                width="100%"
                boxSizing="border-box"
                minHeight="60px"
              >
                <IconButton color="primary" component="label" style={{ padding: "8px" }}>
                  <AttachFileIcon style={{ color: "black" }} />
                  <input
                    type="file"
                    hidden
                    onChange={handleFileUpload}
                    accept=".csv,.xls,.xlsx,.pdf,.jpeg,.png,.jpg,.bmp,.doc,.docx"
                  />
                </IconButton>
                <TextField
                  placeholder="Type your comment here..."
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  InputProps={{
                    style: {
                      height: "40px",
                    },
                  }}
                  inputProps={{
                    style: {
                      padding: "10px 12px",
                    },
                  }}
                />
                {sendMessageLoading ? (
                  <IconButton style={{ marginLeft: "10px", padding: "8px" }}>
                    <CircularProgress color="primary" size="24px" thickness={3} />
                  </IconButton>
                ) : (
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    style={{ marginLeft: "10px", padding: "8px" }}
                  >
                    <SendIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ChatBox;

